import { useCallback, useEffect, useState } from 'react'
import { Button } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { useT } from '@/shared/i18n'
import { decodeJwt, JwtDecodeError } from '@/shared/jwt'
import { ToolPageShell } from '@/shared/tool-shell/ToolPageShell'
import { CodeWorkspace } from '@/shared/ui/code-workspace'

const UTILITY = 'jwt-decoder'

export function JwtDecoderPage() {
  const t = useT()
  const [input, setInput] = useState('')
  const [output, setOutput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const handleDecode = useCallback(() => {
    if (!input.trim()) {
      setOutput('')
      setError(null)
      return
    }

    const start = performance.now()
    toolInvoke(UTILITY, { action: 'decode' })

    try {
      const decoded = decodeJwt(input)
      setOutput(JSON.stringify(decoded, null, 2))
      setError(null)
      toolSuccess(UTILITY, Math.round(performance.now() - start), { action: 'decode' })
    } catch (caught) {
      const code = caught instanceof JwtDecodeError ? caught.code : 'invalid-json'
      setOutput('')
      setError(t.jwtDecoder.errors[code])
      toolError(UTILITY, code, { action: 'decode' })
    }
  }, [input, t])

  useEffect(() => {
    const timeout = window.setTimeout(handleDecode, 200)
    return () => window.clearTimeout(timeout)
  }, [handleDecode])

  const handleClear = useCallback(() => {
    setInput('')
    setOutput('')
    setError(null)
  }, [])

  const handleCopy = useCallback(async () => {
    if (!output) return
    try {
      await navigator.clipboard.writeText(output)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      setCopied(false)
    }
  }, [output])

  return (
    <ToolPageShell toolKey="jwtDecoder" width="wide">
      <CodeWorkspace
        error={error}
        controls={(
          <p className="text-sm text-amber-800 dark:text-amber-200">
              {t.jwtDecoder.signatureWarning}
            </p>
        )}
        input={{
          title: t.jwtDecoder.token,
          value: input,
          clear: { label: t.jwtDecoder.clear, onClick: handleClear },
          onChange: (value) => {
            setInput(value)
            setOutput('')
            setError(null)
          },
          placeholder: t.jwtDecoder.inputPlaceholder,
          ariaLabel: t.jwtDecoder.inputAriaLabel,
          minHeightClassName: 'min-h-[280px] lg:min-h-[420px]',
          textareaClassName: 'break-all',
          wrap: 'soft',
        }}
        output={{
          title: t.jwtDecoder.result,
          value: output,
          readOnly: true,
          placeholder: t.jwtDecoder.outputPlaceholder,
          ariaLabel: t.jwtDecoder.outputAriaLabel,
          minHeightClassName: 'min-h-[280px] lg:min-h-[420px]',
          actions: (
            <Button variant="outline" size="sm" onClick={handleCopy}>
              {copied ? t.jwtDecoder.copied : t.jwtDecoder.copy}
            </Button>
          ),
        }}
      />
    </ToolPageShell>
  )
}
